import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import Card from './Card';
import SectionHeader from './SectionHeader';
import Loading from '../common/Loading';
import { withLanguage } from '../common/Texts';
import { dashboardTokens as t } from './tokens';

const LEVEL_LABELS = ['II', 'III', 'IV', 'V'];

export default function TalentDistribution({
    language,
    loading,
    pieData,
    levels,
    capPotencial,
    colors,
}) {
    const tr = withLanguage(language);
    const total = levels.reduce((a, b) => a + b, 0);

    return (
        <Card sx={{ position: 'relative', height: '100%' }}>
            <Loading loading={loading} language={language}/>
            <Box sx={{ opacity: loading ? 0.1 : 1 }}>
                <SectionHeader
                    title={tr('talent_distribution') || 'Talent Distribution'}
                    subtitle={tr('people_by_level') || 'People by level'}
                />

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Box sx={{ width: 220, height: 220, position: 'relative' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    dataKey="value"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius="68%"
                                    outerRadius="100%"
                                    paddingAngle={0}
                                    stroke="none"
                                    isAnimationActive={true}
                                >
                                    {pieData.map((_, i) => (
                                        <Cell key={i} fill={colors[i % colors.length]}/>
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        <Box sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}>
                            <Typography sx={{ fontSize: 32, fontWeight: 700, color: t.color.fg, lineHeight: 1 }}>
                                {total}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: t.color.fgMuted, mt: 0.5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                {tr('assessed') || 'assessed'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {LEVEL_LABELS.map((label, i) => (
                        <Box key={label} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            py: 0.5,
                        }}>
                            <Box sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '3px',
                                backgroundColor: colors[i],
                                flexShrink: 0,
                            }}/>
                            <Typography sx={{ flex: 1, fontSize: 13, color: t.color.fg }}>
                                {tr('level')} {label}
                            </Typography>
                            <Typography sx={{
                                fontSize: 15,
                                fontWeight: 600,
                                color: t.color.fg,
                                tabularNums: 'tabular-nums',
                            }}>
                                {levels[i]}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Divider sx={{ my: 2, borderColor: t.color.divider }}/>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: t.color.hipo,
                            }}/>
                            <Typography sx={{ fontSize: 13, color: t.color.fgMuted }}>
                                {tr('high_potential')}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: t.color.fg }}>
                            {capPotencial} <Box component="span" sx={{ color: t.color.fgMuted, fontWeight: 400 }}>{tr('people')}</Box>
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Card>
    );
}
